import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { OrderApiService } from './order-api.service';
import { CreateOrderRequest, CreateOrderResponse, Order, OrderListResponse, OrderStatus } from '../interfaces/order';


@Injectable({
  providedIn: 'root'
})
export class OrderService {
  constructor(private orderApi: OrderApiService) { }

  /**
   * Create a new order
   */
  createOrder(request: CreateOrderRequest): Observable<CreateOrderResponse> {
    return this.orderApi.createOrder(request);
  }

  /**
   * Get all orders for the current user
   */
  getMyOrders(page: number = 1, size: number = 20, status?: OrderStatus): Observable<OrderListResponse> {
    return this.orderApi.getMyOrders(page, size, status);
  }

  /**
   * Get a single order by ID
   */
  getOrder(orderId: number): Observable<Order | undefined> {
    return this.orderApi.getOrder(orderId);
  }

  /**
   * Cancel an order
   */
  cancelOrder(orderId: number): Observable<Order | undefined> {
    return this.orderApi.cancelOrder(orderId);
  }

  /**
   * Get order status display info
   */
  getStatusInfo(status: OrderStatus): { label: string; icon: string; color: string } {
    const statusMap: Record<OrderStatus, { label: string; icon: string; color: string }> = {
      [OrderStatus.Pending]: {
        label: 'Ausstehend',
        icon: 'pi-clock',
        color: 'orange'
      },
      [OrderStatus.Confirmed]: {
        label: 'Bestätigt',
        icon: 'pi-check-circle',
        color: 'blue'
      },
      [OrderStatus.ReadyForPickup]: {
        label: 'Abholbereit',
        icon: 'pi-shopping-bag',
        color: 'cyan'
      },
      [OrderStatus.OutForDelivery]: {
        label: 'In Zustellung',
        icon: 'pi-truck',
        color: 'indigo'
      },
      [OrderStatus.Completed]: {
        label: 'Abgeschlossen',
        icon: 'pi-check',
        color: 'green'
      },
      [OrderStatus.Cancelled]: {
        label: 'Storniert',
        icon: 'pi-times-circle',
        color: 'red'
      }
    };

    return statusMap[status] || statusMap[OrderStatus.Pending];
  }

  /**
   * Calculate smart coins discount (100 coins = 1 EUR)
   */
  calculateSmartCoinsDiscount(smartCoins: number): number {
    if (smartCoins < 100) {
      return 0;
    }
    return smartCoins / 100;
  }

  /**
   * Get maximum smart coins that can be used for a given amount
   */
  getMaxUsableSmartCoins(amount: number, availableCoins: number): number {
    const maxCoinsForAmount = Math.floor(amount * 100);
    return Math.min(maxCoinsForAmount, availableCoins);
  }

  /**
   * Format order number for display
   */
  formatOrderNumber(orderNumber: string): string {
    return `#${orderNumber}`;
  }
}